/**
 * Address and phone helpers shared by the checkout and „Moje konto“ – safe on server and client.
 * The design has one „Ulica a číslo domu“ field; the customer record and the order API keep the
 * street and the house number apart.
 */

/** „Hlavná 24“ → { street: 'Hlavná', number: '24' }; „Nám. SNP 12/A“ → number „12/A“. */
export function splitStreet(value: string | null | undefined) {
  const text = (value ?? '').trim().replace(/\s+/g, ' ');
  const match = text.match(/^(.*\D)\s+(\d[\w/-]*)$/);
  return match ? { street: match[1].trim(), number: match[2] } : { street: text, number: '' };
}

/** „Hlavná“ + „24“ → „Hlavná 24“. */
export function joinStreet(street: string | null | undefined, number: string | null | undefined) {
  return [street, number]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

/** Digits with an optional leading „+“ („+421 905 123 456“ → „+421905123456“). */
export function normalizePhone(value: string) {
  const trimmed = value.trim();
  return (trimmed.startsWith('+') ? '+' : '') + trimmed.replace(/\D/g, '');
}

/**
 * E.164 for the API. A number without a country code is Slovak (the store's country): „0905 123 456“
 * and „905 123 456“ (9 digits) get +421; „421 905 …“ (12 digits) gets the „+“.
 */
export function toE164Phone(value: string) {
  const phone = normalizePhone(value);
  if (phone.startsWith('+')) return phone;
  if (phone.startsWith('00')) return '+' + phone.slice(2);
  if (phone.startsWith('0')) return '+421' + phone.slice(1);
  if (phone.length === 9) return '+421' + phone;
  return phone ? '+' + phone : '';
}

/** 9–15 digits after the country code (E.164), as the order API accepts. */
export function isValidPhone(value: string) {
  return /^\+\d{9,15}$/.test(toE164Phone(value));
}

/** „+421905123456“ → „+421 905 123 456“ for display. */
export function formatPhone(value: string | null | undefined) {
  const phone = normalizePhone(value ?? '');
  const sk = phone.match(/^\+421(\d{3})(\d{3})(\d{3})$/);
  return sk ? `+421 ${sk[1]} ${sk[2]} ${sk[3]}` : (value ?? '');
}
